import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from 'designSystem/components/ContextMenu'
import { Input } from 'designSystem/components/Input'
import { Tabs, TabsList, TabsTrigger } from 'designSystem/components/Tabs'
import { Tooltip, TooltipTrigger } from 'designSystem/components/Tooltip'
import { KNOWLEDGE_DOC_TAB_ACTION, useGeckoAnalytics } from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { createDefaultTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { useWorkOrders } from 'features/knowledgeDocumentViewer/workOrders/useWorkOrder'
import {
  cmmsRecordNumber,
  isCmmsRecordId,
} from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { getWorkspaceTabIcon } from 'features/workspaceTabs/utils/getWorkspaceTabIcon'
import { Plus, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDragReorder } from 'utils/useDragReorder'

import {
  DocumentTabTooltipContent,
  TabAddButtonSlot,
  TabCloseButtonSlot,
  TabColorIndicator,
  TabDropIndicator,
  TabEditContainer,
  TabIconSlot,
  TabShell,
  TabTitle,
  TabTitleText,
} from './documentTabsStyles'

const TAB_TITLE_TOOLTIP_DELAY_MS = 500

const TooltipTriggerArea = styled.div`
  width: 100%;
`

function DocumentTabTitleTooltip({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const hoverDelayRef = useRef<number | null>(null)

  const clearHoverDelay = useCallback(() => {
    if (hoverDelayRef.current === null) return

    window.clearTimeout(hoverDelayRef.current)
    hoverDelayRef.current = null
  }, [])

  const handleMouseEnter = useCallback(() => {
    clearHoverDelay()
    hoverDelayRef.current = window.setTimeout(() => {
      setIsOpen(true)
      hoverDelayRef.current = null
    }, TAB_TITLE_TOOLTIP_DELAY_MS)
  }, [clearHoverDelay])

  const handleMouseLeave = useCallback(() => {
    clearHoverDelay()
    setIsOpen(false)
  }, [clearHoverDelay])

  useEffect(() => clearHoverDelay, [clearHoverDelay])

  return (
    <Tooltip open={isOpen}>
      <TooltipTrigger asChild>
        <TooltipTriggerArea
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {children}
        </TooltipTriggerArea>
      </TooltipTrigger>
      <DocumentTabTooltipContent side='bottom' sideOffset={5}>
        {label}
      </DocumentTabTooltipContent>
    </Tooltip>
  )
}

export function DocumentTabs() {
  const {
    openTabs,
    removeTab,
    renameTab,
    reorderTabs,
    activeTabId,
    setActiveTabId,
    addTab,
    tabIndicatorColors,
  } = useKnowledgeDocumentUXContext()
  const { t } = useLingui()
  const { trackEvent } = useGeckoAnalytics()
  const { removeAttachment } = useChatbotAttachments()

  const [renamingTabId, setRenamingTabId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const pendingRenameFocusRef = useRef<string | null>(null)
  const renameStartedAtRef = useRef<number>(0)
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null)

  const tabDrag = useDragReorder<DocumentTab>('tab-id')

  const onTabDragStart = useCallback(
    (e: React.DragEvent, tabId: string) => {
      setDraggingTabId(tabId)
      tabDrag.handleDragStart(e, tabId)
    },
    [tabDrag]
  )

  const onTabDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      tabDrag.handleDrop(e, targetId)
      const reordered = tabDrag.reorder(openTabs)
      if (reordered) {
        // Pinned tabs (closable === false) always stay left-most, regardless of
        // where a drag dropped things.
        const pinned = reordered.filter((t) => t.closable === false)
        const rest = reordered.filter((t) => t.closable !== false)
        reorderTabs(pinned.length ? [...pinned, ...rest] : reordered)
      }
      setDraggingTabId(null)
    },
    [tabDrag, openTabs, reorderTabs]
  )

  const onTabDragEnd = useCallback(() => {
    tabDrag.handleDragEnd()
    setDraggingTabId(null)
  }, [tabDrag])

  const tabDocumentIds = useMemo(
    () =>
      openTabs
        .map((tab) => {
          const item = tab.history[tab.currentIndex]
          return item.type === 'document' ? item.documentId : null
        })
        .filter((id): id is string => id !== null),
    [openTabs]
  )

  const { documents: tabDocuments } = useSystemDocumentsByBqIds(
    tabDocumentIds.filter((id) => !isCmmsRecordId(id))
  )
  const { entries: workOrders } = useWorkOrders(tabDocumentIds)

  const handleTabClick = (tabId: string) => {
    if (renamingTabId === tabId) return
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      const currentItem = tab.history[tab.currentIndex]
      trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
        action: 'switch',
        tab_id: tabId,
        document_id:
          currentItem.type === 'document' ? currentItem.documentId : '',
        total_tabs: openTabs.length,
      })
      setActiveTabId(tabId)
    }
  }

  const handleStartRename = (tabId: string) => {
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      pendingRenameFocusRef.current = tabId
      renameStartedAtRef.current = performance.now()
      setRenameValue(tab.customLabel ?? getReportName(tab))
      setRenamingTabId(tabId)
    }
  }

  const handleConfirmRename = () => {
    if (renamingTabId) {
      renameTab(renamingTabId, renameValue.trim())
      setRenamingTabId(null)
    }
  }

  const handleRenameBlur = () => {
    const elapsedSinceRenameStart =
      performance.now() - renameStartedAtRef.current
    if (elapsedSinceRenameStart < 250) {
      renameInputRef.current?.focus()
      return
    }

    handleConfirmRename()
  }

  const handleCancelRename = () => {
    setRenamingTabId(null)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirmRename()
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    const closedTab = openTabs.find((t) => t.id === tabId)
    const isClosingActiveTab = activeTabId === tabId
    const closedTabItem = closedTab?.history[closedTab.currentIndex]
    const closedTabDocId =
      closedTabItem?.type === 'document' ? closedTabItem.documentId : ''

    removeTab(tabId, () => {
      trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
        action: 'close',
        tab_id: tabId,
        document_id: closedTabDocId,
        total_tabs: openTabs.length,
        was_active: isClosingActiveTab,
      })
      if (closedTabDocId) removeAttachment(closedTabDocId)
    })
  }

  const handleAddTab = useCallback(() => {
    const newTab: DocumentTab = createDefaultTab()
    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'add',
      tab_id: newTab.id,
      total_tabs: openTabs.length + 1,
    })
    addTab(newTab)
  }, [addTab, openTabs.length, trackEvent])

  const getCurrentDocumentFromTab = useCallback(
    (tab: DocumentTab) => {
      const currentItem = tab.history[tab.currentIndex]
      if (currentItem.type === 'document') {
        return tabDocuments?.find(
          (entry) => getSystemDocumentBqId(entry) === currentItem.documentId
        )
      }
      return undefined
    },
    [tabDocuments]
  )

  const getReportName = useCallback(
    (tab: DocumentTab): string => {
      if (tab.customLabel) return tab.customLabel

      const currentItem = tab.history[tab.currentIndex]

      if (
        currentItem.type === 'search' &&
        currentItem.linkedDocuments !== undefined
      ) {
        const referenceText = currentItem.linkedDocuments.referenceText
        return t`References to ${referenceText}`
      }

      if (
        currentItem.type === 'document' &&
        isCmmsRecordId(currentItem.documentId)
      ) {
        const entry = workOrders.find(
          (entry) => entry.id === currentItem.documentId
        )
        return entry ? cmmsRecordNumber(entry) : t`CMMS record`
      }
      const currentDocument = getCurrentDocumentFromTab(tab)
      if (!currentDocument) return t`New Tab`
      return toTitleCase(
        currentDocument.entry.data.document_title ??
          currentDocument.entry.data.filename ??
          t`New Tab`
      )
    },
    [getCurrentDocumentFromTab, t, workOrders]
  )

  // Focus input when rename starts
  useEffect(() => {
    if (!renamingTabId) return

    const focusRenameInput = () => {
      const input = renameInputRef.current
      if (!input) return

      input.focus()
      input.select()
      pendingRenameFocusRef.current = null
    }

    const animationFrame = requestAnimationFrame(focusRenameInput)
    const timeout = window.setTimeout(focusRenameInput, 0)

    return () => {
      cancelAnimationFrame(animationFrame)
      window.clearTimeout(timeout)
    }
  }, [renamingTabId])

  return (
    <Tabs value={activeTabId} onValueChange={handleTabClick}>
      <TabsList
        variant='browser'
        justify='start'
        width='full'
        size='sm'
        aria-label={t`Open documents`}>
        {openTabs.map((tab) => {
          const isRenaming = renamingTabId === tab.id
          const isActive = activeTabId === tab.id
          const tabIndicatorColor = tabIndicatorColors.get(tab.id)
          const reportName = getReportName(tab)
          const TabIcon = getWorkspaceTabIcon(tab)

          return (
            <ContextMenu key={tab.id}>
              <ContextMenuTrigger asChild>
                <TabShell
                  $active={isActive}
                  $dragging={draggingTabId === tab.id}
                  $renaming={isRenaming}
                  draggable={renamingTabId !== tab.id && tab.closable !== false}
                  onDragStart={(e) => onTabDragStart(e, tab.id)}
                  onDragOver={(e) => tabDrag.handleDragOver(e, tab.id)}
                  onDragLeave={tabDrag.handleDragLeave}
                  onDrop={(e) => onTabDrop(e, tab.id)}
                  onDragEnd={onTabDragEnd}>
                  {tabDrag.dropTarget?.id === tab.id && (
                    <TabDropIndicator
                      $position={
                        tabDrag.dropTarget.position === 'before'
                          ? 'before'
                          : 'after'
                      }
                    />
                  )}

                  {isRenaming ? (
                    <TabEditContainer>
                      {tabIndicatorColor && (
                        <TabColorIndicator $color={tabIndicatorColor} />
                      )}
                      <TabIconSlot>
                        <TabIcon />
                      </TabIconSlot>
                      <Input
                        ref={renameInputRef}
                        inputSize='inlineEdit'
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        onBlur={handleRenameBlur}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TabEditContainer>
                  ) : (
                    <DocumentTabTitleTooltip label={reportName}>
                      <TabsTrigger
                        variant='browser'
                        size='sm'
                        justify='start'
                        actionArea='inlineEnd'
                        value={tab.id}>
                        {tabIndicatorColor && (
                          <TabColorIndicator $color={tabIndicatorColor} />
                        )}
                        <TabIconSlot>
                          <TabIcon />
                        </TabIconSlot>
                        <TabTitle>
                          <TabTitleText>{reportName}</TabTitleText>
                        </TabTitle>
                      </TabsTrigger>
                    </DocumentTabTitleTooltip>
                  )}

                  {!isRenaming && tab.closable !== false && (
                    <TabCloseButtonSlot>
                      <Button
                        variant='browserTabCloseAction'
                        size='icon-sm'
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleCloseTab(e, tab.id)}
                        aria-label={t`Close tab`}>
                        <X />
                      </Button>
                    </TabCloseButtonSlot>
                  )}
                </TabShell>
              </ContextMenuTrigger>
              <ContextMenuContent
                onCloseAutoFocus={(event) => {
                  if (pendingRenameFocusRef.current === tab.id) {
                    event.preventDefault()
                  }
                }}>
                <ContextMenuItem onSelect={() => handleStartRename(tab.id)}>
                  {t`Rename`}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          )
        })}
        <TabAddButtonSlot>
          <Button
            variant='browserTabAction'
            size='tab-icon'
            onClick={handleAddTab}
            title={t`Add new tab`}
            aria-label={t`Add new tab`}>
            <Plus />
          </Button>
        </TabAddButtonSlot>
      </TabsList>
    </Tabs>
  )
}
